import { linkLogicRelationships } from "cxa-utils";
import {
  PLAN_BASED_CONVERT_LEAVE,
  SUM_ASSURED_BASED,
  PLAN_BASED,
  PLAN_BASED_OPTIONAL,
} from "./constants";

export const getIntervalOptionPriceTagging = plan => {
  const members = plan.eligibleMembers.members || [];
  const selectionMode =
    plan.eligibleMembers.selectionMode ||
    (members && members.length > 0 && members[0].selectionMode) ||
    "";

  if (selectionMode === "Individual") {
    const selectedMembers = members.filter(m => m.isSelected);
    if (Array.isArray(selectedMembers) && selectedMembers.length <= 0) {
      return 0;
    }
    const selectedIntervalOption =
      plan.intervalOptions.find(option => option.isSelected) || plan.intervalOptions[0];
    let price = 0;
    if (selectedIntervalOption) {
      const memberPrice = selectedIntervalOption.memberPrices.filter(mp =>
        selectedMembers.find(member => member.key === mp.memberKey)
      );
      memberPrice.forEach(mp => {
        price += parseFloat(mp.price.numericValue);
      });
    }
    return price;
  } else {
    return parseFloat(plan.pricing.price.numericValue).toFixed(2);
  }
};

export const getPriceTagging = (priceTagMode, members, plan) => {
  let price = 0,
    relativePrice = 0,
    relativeTagNode = "";
  Array.isArray(members) &&
    members.forEach(item => {
      if (priceTagMode === "RelativePrice") {
        relativePrice += parseFloat(item.relativePrice ? item.relativePrice.numericValue : "0");
      }
      price += parseFloat((item.price && item.price.numericValue) || 0);
    });

  if (isNaN(price) || price == 0) {
    price = (plan.pricing && plan.pricing.price && plan.pricing.price.numericValue) || 0;
  }
  if (
    priceTagMode === "RelativePrice" &&
    plan.eligibleMembers &&
    Array.isArray(plan.eligibleMembers.members) &&
    plan.eligibleMembers.members.length > 0
  ) {
    const selectedMember =
      plan.eligibleMembers.members.find(x => x.type == "Employee") ||
      plan.eligibleMembers.members[0];
    if (selectedMember) {
      relativeTagNode =
        selectedMember.relativePrice && selectedMember.relativePrice.relativeTagNode;
    }
  }
  return {
    isRelativePrice: priceTagMode === "RelativePrice",
    price,
    relativePrice,
    relativeTagNode,
  };
};

export const getIndividualPriceTagging = (priceTagMode, members, planType) => {
  let price = 0,
    relativePrice = 0,
    relativeTagNode = "";
  const selectedMembers = members.filter(x => x.isSelected);
  Array.isArray(members) &&
    selectedMembers.forEach(item => {
      if (priceTagMode === "RelativePrice") {
        relativeTagNode = item.relativePrice && item.relativePrice.relativeTagNode;
        relativePrice += parseFloat(item.relativePrice ? item.relativePrice.numericValue : "0");
        price += parseFloat(item.price.numericValue);
      } else if (planType === "Group" || item.type !== "Employee") {
        price += parseFloat(item.price.numericValue);
      }
    });

  return {
    isRelativePrice: priceTagMode === "RelativePrice",
    price,
    relativePrice,
    relativeTagNode,
  };
};

export const getProductPrice = (benefit, priceTagMode) => {
  const planType = benefit.benefitOptions.planType;
  const selectedPlan = benefit.benefitOptions.options.find(x => x.isSelected);

  if (!selectedPlan) return 0;
  if (isPlanMemberGrouped(selectedPlan)) {
    const selectedMemberGroup = (
      selectedPlan.plans.find(x => x.isSelected) || selectedPlan.plans[0]
    ).eligibleMembers;
    return getPriceTagging(priceTagMode, selectedMemberGroup.members, selectedPlan).price;
  }

  const eligibleMembers = selectedPlan.eligibleMembers;
  const selectionMode = eligibleMembers.selectionMode;
  const hasIntervalOptions =
    Array.isArray(selectedPlan.intervalOptions) && selectedPlan.intervalOptions.length > 0;
  if (hasIntervalOptions) {
    return Number(getIntervalOptionPriceTagging(selectedPlan));
  }

  const pricing =
    selectionMode === "Individual"
      ? getIndividualPriceTagging(priceTagMode, eligibleMembers.members, planType)
      : getPriceTagging(priceTagMode, eligibleMembers.members, selectedPlan);
  return Number(pricing.price);
};

function compareMemberKeys(arr1, arr2) {
  if (arr1.length !== arr2.length) {
    return false;
  }

  // Sort the arrays
  const sortedArr1 = arr1.slice().sort();
  const sortedArr2 = arr2.slice().sort();

  for (let i = 0; i < sortedArr1.length; i++) {
    if (sortedArr1[i] !== sortedArr2[i]) {
      return false;
    }
  }

  return true;
}

export const isDefaultPlan = benefit => {
  const selectedPlan = benefit.benefitOptions.options.find(x => x.isSelected);
  if (!selectedPlan) return false;
  const defaultPlan = benefit.benefitOptions.defaultOption;

  if (selectedPlan.id === defaultPlan.id) {
    if (
      selectedPlan.id === "0000" ||
      selectedPlan.id === "0n" ||
      (selectedPlan.eligibleMembers && selectedPlan.eligibleMembers.selectionMode !== "Individual")
    ) {
      return true;
    }
  } else {
    if (isGroupPlanPerMembered(selectedPlan)) {
      const selectedMemberGroup = selectedPlan.plans.find(x => x.isSelected);
      if (selectedMemberGroup && selectedMemberGroup.id === defaultPlan.id) {
        if (selectedMemberGroup.id === "0000" || selectedMemberGroup.id === "0n") {
          return true;
        } else {
          const selectedMembers =
            selectedPlan.eligibleMembers.members.filter(x => x.isSelected) || [];
          const memberKeys = selectedMembers.reduce((accumulated, member) => {
            return accumulated.concat(member.key);
          }, []);
          const defaultMemberKeys =
            defaultPlan.members &&
            defaultPlan.members.reduce((accumulated, member) => {
              return accumulated.concat(member.key);
            }, []);
          return compareMemberKeys(memberKeys, defaultMemberKeys);
        }
      }
    }

    if (isGroupPlan(selectedPlan)) {
      const selectedMemberGroup = selectedPlan.plans.find(x => x.isSelected);
      if (selectedMemberGroup && selectedMemberGroup.id === defaultPlan.id) {
        return true;
      }
    }
  }
  return false;
};

export const calculateUtilization = enrolmentData => {
  const {
    planBasedConvertLeaveBenefits,
    sumAssuredBasedBenefits,
    planBasedBenefits,
    planBasedOptionalBenefits,
    priceTagMode,
  } = enrolmentData;

  let entitlement = 0;
  let utilization = 0;
  planBasedConvertLeaveBenefits.forEach(benefit => {
    const benefitPrice = getProductPrice(benefit, priceTagMode);
    if (benefitPrice < 0) {
      entitlement += Math.abs(benefitPrice);
    }
    if (benefitPrice > 0) {
      utilization += Math.abs(benefitPrice);
    }
  });

  [...sumAssuredBasedBenefits, ...planBasedBenefits, ...planBasedOptionalBenefits].forEach(
    benefit => {
      const benefitPrice = getProductPrice(benefit, priceTagMode);
      if (benefitPrice > 0) {
        utilization += benefitPrice;
      } else {
        entitlement += Math.abs(benefitPrice);
      }
    }
  );
  return [entitlement, utilization];
};

export const isPlanMemberGrouped = plan =>
  plan &&
  plan.groupID &&
  Array.isArray(plan.plans) &&
  (plan.plans.length > 1 ||
    (plan.plans.length == 1 && plan.plans.some(x => ["EF", "ES", "EC"].includes(x.memberType)))) &&
  plan.plans.some(x => x.eligibleMembers.selectionMode !== "Individual");

export const isGroupPlan = plan => plan.groupID && Array.isArray(plan.plans);

export const isGroupPlanPerMembered = plan => {
  return (
    plan.groupID &&
    Array.isArray(plan.plans) &&
    plan.plans.length > 0 &&
    plan.plans.some(x => x.eligibleMembers.selectionMode === "Individual")
  );
};
export const isPlanPerMembered = plan => {
  const eligibleMembers =
    plan &&
    (plan.eligibleMembers ||
      (Array.isArray(plan.plans) && plan.plans.length > 0 && plan.plans[0].eligibleMembers));
  return eligibleMembers.selectionMode === "Individual";
};

export const isPlanWithIntervalOptions = plan =>
  plan.intervalOptions && plan.intervalOptions.length > 0;

export const getIntervalOptionMemberPrices = plan => {
  const intervalOption =
    plan.intervalOptions &&
    plan.intervalOptions.length > 0 &&
    plan.intervalOptions.find(option => option.isSelected);
  return intervalOption ? intervalOption.memberPrices : null;
};

export const getMemberGroupByType = (priceLabelName, memberType, intl) => {
  const messages = {
    EO: "Employee Only",
    EE: "Employee Only",
    ES: "Employee & Spouse Only",
    EC: "Employee & Children Only",
    EF: "Employee & Family Only",
    SO: "Spouse Only",
    CO: "Children Only",
    DO: "Spouse and Children",
  };

  const message = messages[memberType];
  return message ? intl.formatMessage({ id: message, defaultMessage: message }) : priceLabelName;
};

export const formatMoney = number => {
  return parseFloat(number).toLocaleString("en-US", {
    style: "decimal",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const transformBenefits = benefits => {
  return benefits.map(benefit => {
    return transformBenefit(benefit);
  });
};

const transformBenefit = benefit => {
  const selectedPlan =
    benefit.benefitOptions &&
    (benefit.benefitOptions.selectedOption || benefit.benefitOptions.defaultOption);
  return {
    ...benefit,
    isExpanded: true,
    isShownMore: false,
    benefitOptions: {
      ...benefit.benefitOptions,
      options: benefit.benefitOptions.options.map(plan => {
        //handle grouped plans
        if (plan.plans && Array.isArray(plan.plans)) {
          const selectedOption = plan.plans.find(x => x.id === selectedPlan.id);
          const allMembers = getAllEligibleMembers(plan);
          if (!selectedOption) {
            const eligibleMembers = plan.plans[0].eligibleMembers;
            return {
              ...plan,
              isSelected: false,
              isAvailable: true,
              eligibleMembers: {
                ...eligibleMembers,
                members: allMembers.map(x => {
                  return { ...x, isSelected: x.type === "Employee", selectable: true };
                }),
              },
              plans: plan.plans.map(x => ({ ...x, isSelected: false, isAvailable: true })),
            };
          } else {
            const selectedMembers = selectedPlan.members || [];
            const eligibleMembers = selectedOption.eligibleMembers;
            return {
              ...plan,
              isSelected: true,
              isAvailable: true,
              eligibleMembers: {
                ...eligibleMembers,
                members: allMembers.map(x => {
                  if (
                    selectedMembers.find(s => s.key === x.key && s.type === x.type) &&
                    plan.plans.find(x => x.id === selectedOption.id)
                  ) {
                    return { ...x, isSelected: true, selectable: true };
                  }
                  return { ...x, isSelected: x.type === "Employee", selectable: true };
                }),
              },
              plans: plan.plans.map(x => {
                if (x.id === selectedOption.id) {
                  return { ...x, isSelected: true, isAvailable: true };
                }
                return { ...x, isSelected: false, isAvailable: true };
              }),
            };
          }
        } else {
          //handle non-grouped plans

          const hasIntervalOptions =
            Array.isArray(plan.intervalOptions) && plan.intervalOptions.length > 0;

          if (hasIntervalOptions && selectedPlan.id.startsWith(plan.id)) {
            const selectedIndex = selectedPlan.id.split("i")[1] || 0;
            const selectionMode = plan.eligibleMembers.selectionMode;
            const selectedMembers = selectedPlan.members;
            const accumulatedPrice = plan.intervalOptions[selectedIndex].memberPrices.reduce(
              (accumulator, memberPrice) => {
                if (selectedMembers.find(x => x === memberPrice.memberKey)) {
                  return {
                    numericValue:
                      Number(accumulator.numericValue || 0) +
                      Number(memberPrice.price.numericValue),
                    value: Number(accumulator.value || 0) + Number(memberPrice.price.value),
                  };
                }
                return accumulator;
              },
              { numericValue: 0, value: 0 }
            );
            return {
              ...plan,
              intervalOptions: plan.intervalOptions.map((x, idx) =>
                idx == selectedIndex ? { ...x, isSelected: true } : { ...x }
              ),
              originalIntervalOptions: [...plan.intervalOptions],
              eligibleMembers:
                Array.isArray(selectedMembers) && selectedMembers.length > 0
                  ? {
                      ...plan.eligibleMembers,
                      members: plan.eligibleMembers.members.map(x =>
                        selectedMembers.find(s => s.key === x.key)
                          ? { ...x, isSelected: true, isAvailable: true }
                          : { ...x, isSelected: false, isAvailable: true }
                      ),
                    }
                  : { ...plan.eligibleMembers },
              planValue: { ...plan.intervalOptions[selectedIndex].planValue },
              pricing: {
                ...plan.pricing,
                price:
                  Array.isArray(selectedMembers) &&
                  selectedMembers.length > 0 &&
                  selectionMode === "Individual"
                    ? accumulatedPrice
                    : plan.intervalOptions[selectedIndex].price,
              },
              isSelected: true,
              isAvailable: true,
            };
          }
          if (hasIntervalOptions) {
            return {
              ...plan,
              originalIntervalOptions: [...plan.intervalOptions],
              isSelected: false,
              isAvailable: true,
            };
          } else if (plan.id === selectedPlan.id) {
            return { ...plan, isSelected: true, isAvailable: true };
          } else {
            return { ...plan, isSelected: false, isAvailable: true };
          }
        }
      }),
    },
  };
};

export const initEnrollmentDetail = enrollmentDetail => {
  const data = {
    ...enrollmentDetail,
    planBasedConvertLeaveBenefits: transformBenefits(
      enrollmentDetail.planBasedConvertLeaveBenefits || []
    ),
    sumAssuredBasedBenefits: transformBenefits(enrollmentDetail.sumAssuredBasedBenefits || []),
    planBasedBenefits: transformBenefits(enrollmentDetail.planBasedBenefits || []),
    planBasedOptionalBenefits: transformBenefits(enrollmentDetail.planBasedOptionalBenefits || []),
  };

  return applyLinkLogic(data);
};
export const buildBenefitMap = enrollmentDetail => {
  const mapping = {};
  enrollmentDetail.planBasedConvertLeaveBenefits.forEach(x => {
    mapping[x.benefitID] = x.benefitName;
  });
  enrollmentDetail.sumAssuredBasedBenefits.forEach(x => {
    mapping[x.benefitID] = x.benefitName;
  });
  enrollmentDetail.planBasedBenefits.forEach(x => {
    mapping[x.benefitID] = x.benefitName;
  });
  enrollmentDetail.planBasedOptionalBenefits.forEach(x => {
    mapping[x.benefitID] = x.benefitName;
  });
  return mapping;
};

export const updateStepsBasedOnEnrollmentDetail = (enrollmentDetail, steps) => {
  let updatedSteps = steps;
  if (
    Array.isArray(enrollmentDetail.planBasedConvertLeaveBenefits) &&
    enrollmentDetail.planBasedConvertLeaveBenefits.length <= 0
  ) {
    updatedSteps = [...updatedSteps.filter(x => x.benefitTypeId != PLAN_BASED_CONVERT_LEAVE)];
  }
  if (
    Array.isArray(enrollmentDetail.sumAssuredBasedBenefits) &&
    enrollmentDetail.sumAssuredBasedBenefits.length <= 0
  ) {
    updatedSteps = [...updatedSteps.filter(x => x.benefitTypeId != SUM_ASSURED_BASED)];
  }
  if (
    Array.isArray(enrollmentDetail.planBasedBenefits) &&
    enrollmentDetail.planBasedBenefits.length <= 0
  ) {
    updatedSteps = [...updatedSteps.filter(x => x.benefitTypeId != PLAN_BASED)];
  }
  if (
    Array.isArray(enrollmentDetail.planBasedOptionalBenefits) &&
    enrollmentDetail.planBasedOptionalBenefits.length <= 0
  ) {
    updatedSteps = [...updatedSteps.filter(x => x.benefitTypeId != PLAN_BASED_OPTIONAL)];
  }

  return updatedSteps;
};

const transformBenefitForSaving = benefit => {
  const selectedPlan = benefit.benefitOptions.options.find(x => x.isSelected);
  const selectedPlanId = getSelectedPlanId(selectedPlan);
  const isSumAssured = benefit.type === SUM_ASSURED_BASED;
  const sumAssuredAmount =
    isSumAssured && selectedPlan && selectedPlan.planValue
      ? Number(selectedPlan.planValue.numericValue)
      : 0;
  const eligibleMembers =
    selectedPlan &&
    (selectedPlan.eligibleMembers ||
      (Array.isArray(selectedPlan.plans) &&
        selectedPlan.plans.length > 0 &&
        selectedPlan.plans[0].eligibleMembers));

  const getDependantList = selectionMode => {
    if (selectionMode === "Individual") {
      const selectedMembers = eligibleMembers.members.filter(
        x => x.isSelected && x.type !== "Employee"
      );
      return selectedMembers.reduce((arr, member) => {
        return [...arr, member.key];
      }, []);
    } else if (
      selectionMode === "OneInAllIn" &&
      selectedPlan.groupID &&
      Array.isArray(selectedPlan.plans)
    ) {
      const selectedOption = selectedPlan.plans.find(x => x.isSelected);
      return (
        selectedOption &&
        selectedOption.eligibleMembers.members.reduce((depArr, member) => {
          if (member.type !== "Employee") depArr.push(member.key);
          return depArr;
        }, [])
      );
    }

    return [];
  };

  const dependantList = (selectedPlan && getDependantList(eligibleMembers.selectionMode)) || [];

  return {
    Benefit: benefit.benefitID,
    Selection: selectedPlanId,
    IsSumAssured: isSumAssured,
    SumAssured: sumAssuredAmount,
    HasEmployee: true,
    Dependants: dependantList,
  };
};

const SPOUSE = "Spouse";
const CHILDREN = "Children";
const EMPLOYEE = "Employee";

const containChildrenOnly = member => member.type === CHILDREN;
const containSpouseOnly = member => member.type === SPOUSE;
const containFamily = memberTypeList =>
  [SPOUSE, CHILDREN].every(memberType => memberTypeList.includes(memberType));

const containEmployeeSpouse = memberTypeList =>
  [EMPLOYEE, SPOUSE].every(memberType => memberTypeList.includes(memberType));

const containEmployeeChildren = memberTypeList =>
  [EMPLOYEE, CHILDREN].every(memberType => memberTypeList.includes(memberType));

const isChildrenOnly = memberTypeList =>
  !memberTypeList.map(member => member === CHILDREN).includes(false);

const isSpouseOnly = memberTypeList =>
  !memberTypeList.map(member => member === SPOUSE).includes(false);

const isDependentOnly = memberTypeList =>
  [EMPLOYEE].every(memberType => !memberTypeList.includes(memberType));

const filterMemberTypeSelected = filterSelectedMembers =>
  filterSelectedMembers.map(member => {
    if (containSpouseOnly(member) || containChildrenOnly(member)) {
      return member.type;
    }

    return member.type;
  });

const getSelectedMemberTypeForPlanBasedBenefit = eligibleMembers => {
  const selectedMember = eligibleMembers.members.filter(
    member => member.isSelected || member.type === "Employee"
  );

  const filterMemberTypes = filterMemberTypeSelected(selectedMember);
  const isSpouse = selectedMember.length > 0 ? containEmployeeSpouse(filterMemberTypes) : false;
  const isChildren = selectedMember.length > 0 ? containEmployeeChildren(filterMemberTypes) : false;
  const isCO = selectedMember.length > 0 ? isChildrenOnly(filterMemberTypes) : false;
  const isSO = selectedMember.length > 0 ? isSpouseOnly(filterMemberTypes) : false;
  const isDO = selectedMember.length > 0 ? isDependentOnly(filterMemberTypes) : false;
  const isFamily = isDO ? false : containFamily(filterMemberTypes);

  let memberType = "EE";

  if (isFamily) {
    memberType = "EF";
  } else if (isSpouse) {
    memberType = "ES";
  } else if (isChildren) {
    memberType = "EC";
  } else if (isCO) {
    memberType = "CO";
  } else if (isSO) {
    memberType = "SO";
  } else if (isDO) {
    memberType = "DO";
  }
  return memberType;
};
const getPlanByMemberSelectionForPlanBasedBenefit = plan => {
  const memberType = getSelectedMemberTypeForPlanBasedBenefit(plan.eligibleMembers);
  const newPlan = {
    ...plan,
    plans: plan.plans.map(p => {
      return { ...p, isSelected: p.memberType === memberType ? true : false };
    }),
  };
  return newPlan.plans.find(p => p.isSelected);
};

const getSelectedPlanId = plan => {
  if (!plan) return "";
  const hasIntervalOptions = Array.isArray(plan.intervalOptions) && plan.intervalOptions.length > 0;
  if (hasIntervalOptions) {
    const selectedOption = plan.intervalOptions.find(x => x.isSelected);
    return plan.id + (selectedOption ? selectedOption.interval : "0");
  }
  if (plan.groupID && Array.isArray(plan.plans)) {
    if (
      plan.eligibleMembers.selectionMode !== "Individual" ||
      (plan.eligibleMembers.selectionMode === "Individual" && plan.groupValue === "0")
    ) {
      const selectedOption = plan.plans.find(x => x.isSelected);
      return selectedOption && selectedOption.id;
    } else {
      const selectedOption = getPlanByMemberSelectionForPlanBasedBenefit(plan);
      return selectedOption && selectedOption.id;
    }
  } else {
    return plan.id;
  }
};

export const prepareSaveData = enrolmentData => {
  const selectedBenefits = [];
  const {
    planBasedConvertLeaveBenefits,
    sumAssuredBasedBenefits,
    planBasedBenefits,
    planBasedOptionalBenefits,
  } = enrolmentData;
  planBasedConvertLeaveBenefits &&
    planBasedConvertLeaveBenefits.forEach(benefit => {
      selectedBenefits.push(transformBenefitForSaving(benefit));
    });
  sumAssuredBasedBenefits &&
    sumAssuredBasedBenefits.forEach(benefit => {
      selectedBenefits.push(transformBenefitForSaving(benefit));
    });
  planBasedBenefits &&
    planBasedBenefits.forEach(benefit => {
      selectedBenefits.push(transformBenefitForSaving(benefit));
    });
  planBasedOptionalBenefits &&
    planBasedOptionalBenefits.forEach(benefit => {
      selectedBenefits.push(transformBenefitForSaving(benefit));
    });

  return selectedBenefits;
};

export const prepareValidationData = enrolmentData => {
  const validationData = {
    planBasedConvertLeaveBenefits: [],
    sumAssuredBasedBenefits: [],
    planBasedBenefits: [],
    planBasedOptionalBenefits: [],
  };
  const {
    planBasedConvertLeaveBenefits,
    sumAssuredBasedBenefits,
    planBasedBenefits,
    planBasedOptionalBenefits,
  } = enrolmentData;
  planBasedConvertLeaveBenefits &&
    planBasedConvertLeaveBenefits.forEach(benefit => {
      validationData.planBasedConvertLeaveBenefits.push(transformBenefitForSaving(benefit));
    });
  sumAssuredBasedBenefits &&
    sumAssuredBasedBenefits.forEach(benefit => {
      validationData.sumAssuredBasedBenefits.push(transformBenefitForSaving(benefit));
    });
  planBasedBenefits &&
    planBasedBenefits.forEach(benefit => {
      validationData.planBasedBenefits.push(transformBenefitForSaving(benefit));
    });
  planBasedOptionalBenefits &&
    planBasedOptionalBenefits.forEach(benefit => {
      validationData.planBasedOptionalBenefits.push(transformBenefitForSaving(benefit));
    });

  return validationData;
};

const changeSelectedBenefits = (benefits, benefitId, selectedPlan) => {
  return benefits.map(benefit => {
    if (benefit.benefitID === benefitId) {
      const currentPlan = benefit.benefitOptions.options.find(x => x.isSelected);
      const currentMemberGroup =
        currentPlan &&
        Array.isArray(currentPlan.plans) &&
        currentPlan.plans.find(x => x.isSelected);
      const newBenefit = {
        ...benefit,
        benefitOptions: {
          ...benefit.benefitOptions,
          options: benefit.benefitOptions.options.map(plan => {
            //handle grouped plans
            if (plan.plans && Array.isArray(plan.plans) && plan.plans.length > 1) {
              if (plan.groupID === selectedPlan.groupID) {
                return {
                  ...plan,
                  isSelected: true,
                  plans: plan.plans.map(x => {
                    const isGroupMemberSelected =
                      x.eligibleMembers &&
                      x.eligibleMembers.selectionMode !== "Individual" &&
                      currentMemberGroup &&
                      x.memberType === currentMemberGroup.memberType;

                    const selectedPerMemberPlan =
                      plan.eligibleMembers &&
                      plan.eligibleMembers.selectionMode === "Individual" &&
                      getPlanByMemberSelectionForPlanBasedBenefit(plan);
                    return {
                      ...x,
                      isSelected:
                        isGroupMemberSelected ||
                        (selectedPerMemberPlan &&
                          selectedPerMemberPlan.memberType === x.memberType),
                    };
                  }),
                };
              } else {
                return {
                  ...plan,
                  isSelected: false,
                  plans: plan.plans.map(x => ({ ...x, isSelected: false })),
                };
              }
            } else if (plan.plans && Array.isArray(plan.plans) && plan.plans.length == 1) {
              if (plan.groupID === selectedPlan.groupID) {
                return {
                  ...plan,
                  isSelected: true,
                  plans: plan.plans.map(x => ({
                    ...x,
                    isSelected: true,
                  })),
                };
              } else {
                return {
                  ...plan,
                  isSelected: false,
                  plans: plan.plans.map(x => ({ ...x, isSelected: false })),
                };
              }
            } else {
              //handle non-grouped plans
              if (plan.id === selectedPlan.id) {
                return { ...plan, isSelected: true };
              }
              return { ...plan, isSelected: false };
            }
          }),
        },
      };
      const newSelectedPlan = newBenefit.benefitOptions.options.find(x => x.isSelected);
      const newSelectedMemberGroup =
        newSelectedPlan &&
        Array.isArray(newSelectedPlan.plans) &&
        newSelectedPlan.plans.find(x => x.isSelected);
      if (!newSelectedMemberGroup && newSelectedPlan.plans && newSelectedPlan.plans.length > 0) {
        newSelectedPlan.plans[0].isSelected = true;
      }
      return newBenefit;
    }
    return benefit;
  });
};

const changeSelectedMemberGroup = (benefits, benefitId, selectedPlan, memberType) => {
  return benefits.map(benefit => {
    if (benefit.benefitID == benefitId) {
      return {
        ...benefit,
        benefitOptions: {
          ...benefit.benefitOptions,
          options: benefit.benefitOptions.options.map(plan => {
            //handle grouped plans
            if (plan.plans && Array.isArray(plan.plans)) {
              if (plan.groupID === selectedPlan.groupID) {
                return {
                  ...plan,
                  isSelected: true,
                  plans: plan.plans.map(x => ({
                    ...x,
                    isSelected: x.memberType === memberType ? true : false,
                  })),
                };
              } else {
                return {
                  ...plan,
                  isSelected: false,
                  plans: plan.plans.map(x => ({ ...x, isSelected: false })),
                };
              }
            }
          }),
        },
      };
    }
    return benefit;
  });
};

export const updateBenefitsForPlanChange = (enrollmentDetail, benefitId, selectedPlan) => {
  const data = {
    ...enrollmentDetail,
    planBasedConvertLeaveBenefits: changeSelectedBenefits(
      enrollmentDetail.planBasedConvertLeaveBenefits,
      benefitId,
      selectedPlan
    ),
    sumAssuredBasedBenefits: changeSelectedBenefits(
      enrollmentDetail.sumAssuredBasedBenefits,
      benefitId,
      selectedPlan
    ),
    planBasedBenefits: changeSelectedBenefits(
      enrollmentDetail.planBasedBenefits,
      benefitId,
      selectedPlan
    ),
    planBasedOptionalBenefits: changeSelectedBenefits(
      enrollmentDetail.planBasedOptionalBenefits,
      benefitId,
      selectedPlan
    ),
  };

  return applyLinkLogic(data, benefitId, selectedPlan);
};

export const updateBenefitsForMemberGroupChange = (
  enrollmentDetail,
  benefitId,
  selectedPlan,
  memberType
) => {
  const data = {
    ...enrollmentDetail,
    planBasedConvertLeaveBenefits: changeSelectedMemberGroup(
      enrollmentDetail.planBasedConvertLeaveBenefits,
      benefitId,
      selectedPlan,
      memberType
    ),
    sumAssuredBasedBenefits: changeSelectedMemberGroup(
      enrollmentDetail.sumAssuredBasedBenefits,
      benefitId,
      selectedPlan,
      memberType
    ),
    planBasedBenefits: changeSelectedMemberGroup(
      enrollmentDetail.planBasedBenefits,
      benefitId,
      selectedPlan,
      memberType
    ),
    planBasedOptionalBenefits: changeSelectedMemberGroup(
      enrollmentDetail.planBasedOptionalBenefits,
      benefitId,
      selectedPlan,
      memberType
    ),
  };
  return applyLinkLogic(data, benefitId, selectedPlan, memberType);
};

export const updateBenefitsForIntervalOptionChange = (
  enrollmentDetail,
  selectedPlan,
  benefitId,
  optionId
) => {
  const benefitListSA = enrollmentDetail.sumAssuredBasedBenefits.map(benefit => {
    if (benefit.benefitID === benefitId) {
      return {
        ...benefit,
        benefitOptions: {
          ...benefit.benefitOptions,
          options: benefit.benefitOptions.options.map(plan => {
            if (plan.id === selectedPlan.id) {
              const selectedOption = plan.intervalOptions.find(x => x.interval === optionId);
              if (selectedOption) {
                const memberPrices = (selectedOption && selectedOption.memberPrices) || [];
                const selectedMembers = plan.eligibleMembers.members.filter(x => x.isSelected);

                const accumulatedPrice = memberPrices.reduce((accumulator, memberPrice) => {
                  if (selectedMembers.find(x => x.key === memberPrice.memberKey)) {
                    return {
                      numericValue:
                        Number(accumulator.numericValue || 0) +
                        Number(memberPrice.price.numericValue),
                      value: Number(accumulator.value || 0) + Number(memberPrice.price.value),
                    };
                  }
                  return accumulator;
                }, selectedOption.price);
                return {
                  ...plan,
                  isSelected: true,
                  planValue: { ...selectedOption.planValue },
                  pricing: { ...plan.pricing, price: { ...accumulatedPrice } },
                  requiresUW: selectedOption.requiresUW,
                  intervalOptions: [
                    ...plan.intervalOptions.map(option => {
                      if (option.interval === optionId) {
                        return { ...option, isSelected: true };
                      }
                      return { ...option, isSelected: false };
                    }),
                  ],
                };
              }
              return plan;
            }
            return { ...plan, isSelected: false };
          }),
        },
      };
    }
    return benefit;
  });

  const data = { ...enrollmentDetail, sumAssuredBasedBenefits: benefitListSA };
  return applyLinkLogic(data, benefitId, selectedPlan);
};

export const updateBenefitsForMemberSelectionChange = (
  enrollmentDetail,
  selectedPlan,
  benefitId,
  memberKey,
  memberType
) => {
  const data = {
    ...enrollmentDetail,
    planBasedConvertLeaveBenefits: getPlanBasedBenefitsForMemberSelectionChange(
      enrollmentDetail.planBasedConvertLeaveBenefits,
      selectedPlan.groupID,
      benefitId,
      memberKey,
      memberType
    ),
    sumAssuredBasedBenefits: getSumAssuredBenefitsForMemberSelectionChange(
      enrollmentDetail.sumAssuredBasedBenefits,
      selectedPlan.id,
      benefitId,
      memberKey
    ),
    planBasedBenefits: getPlanBasedBenefitsForMemberSelectionChange(
      enrollmentDetail.planBasedBenefits,
      selectedPlan.groupID,
      benefitId,
      memberKey,
      memberType
    ),
    planBasedOptionalBenefits: getPlanBasedBenefitsForMemberSelectionChange(
      enrollmentDetail.planBasedOptionalBenefits,
      selectedPlan.groupID,
      benefitId,
      memberKey,
      memberType
    ),
  };
  return applyLinkLogic(data, benefitId, selectedPlan);
};
const getSumAssuredBenefitsForMemberSelectionChange = (benefits, planId, benefitId, memberKey) => {
  return benefits.map(benefit => {
    if (benefit.benefitID === benefitId) {
      return {
        ...benefit,
        benefitOptions: {
          ...benefit.benefitOptions,
          options: benefit.benefitOptions.options.map(plan => {
            if (plan.id === planId) {
              const selectedOption =
                (Array.isArray(plan.intervalOptions) &&
                  plan.intervalOptions.length > 0 &&
                  plan.intervalOptions.find(x => x.isSelected)) ||
                plan.intervalOptions[0];
              const memberPrices = (selectedOption && selectedOption.memberPrices) || [];
              const selectedMember = plan.eligibleMembers.members.find(x => x.key == memberKey);
              const selectedMemberPrice = memberPrices.find(x => x.memberKey == memberKey);
              if (selectedMemberPrice && selectedMember) {
                return {
                  ...plan,
                  pricing: {
                    ...plan.pricing,
                    price: selectedMember.isSelected
                      ? {
                          numericValue:
                            Number(plan.pricing.price.numericValue || 0) -
                            Number(selectedMemberPrice.price.numericValue),
                          value:
                            Number(plan.pricing.price.value || 0) -
                            Number(selectedMemberPrice.price.value),
                        }
                      : {
                          numericValue:
                            Number(plan.pricing.price.numericValue || 0) +
                            Number(selectedMemberPrice.price.numericValue),
                          value:
                            Number(plan.pricing.price.value || 0) +
                            Number(selectedMemberPrice.price.value),
                        },
                  },
                  eligibleMembers: {
                    ...plan.eligibleMembers,
                    members: plan.eligibleMembers.members.map(member => {
                      if (member.key == memberKey) {
                        return { ...member, isSelected: !member.isSelected };
                      }
                      return member;
                    }),
                  },
                };
              }
              return plan;
            }
            return plan;
          }),
        },
      };
    }
    return benefit;
  });
};

const getPlanBasedBenefitsForMemberSelectionChange = (
  benefits,
  groupID,
  benefitId,
  memberKey,
  memberType
) => {
  return benefits.map(benefit => {
    if (benefit.benefitID === benefitId) {
      return {
        ...benefit,
        benefitOptions: {
          ...benefit.benefitOptions,
          options: benefit.benefitOptions.options.map(plan => {
            if (plan.groupID === groupID) {
              const selectedMember = plan.eligibleMembers.members.find(
                x => x.key == memberKey && x.type == memberType
              );
              if (selectedMember) {
                const updatedEligibleMembers = {
                  ...plan.eligibleMembers,
                  members: plan.eligibleMembers.members.map(member => {
                    if (member.key == memberKey && member.type == memberType) {
                      return { ...member, isSelected: !member.isSelected };
                    }
                    return member;
                  }),
                };

                const selectedMemberType = getSelectedMemberTypeForPlanBasedBenefit(
                  updatedEligibleMembers
                );
                return {
                  ...plan,
                  eligibleMembers: updatedEligibleMembers,
                  plans: plan.plans.map(x => {
                    return { ...x, isSelected: x.memberType === selectedMemberType };
                  }),
                };
              }
              return plan;
            }
            return plan;
          }),
        },
      };
    }
    return benefit;
  });
};

export const updateBenefitsForPanelToggle = (enrollmentDetail, benefitId) => {
  return {
    ...enrollmentDetail,
    planBasedConvertLeaveBenefits: toggleBenefit(
      enrollmentDetail.planBasedConvertLeaveBenefits,
      benefitId
    ),
    sumAssuredBasedBenefits: toggleBenefit(enrollmentDetail.sumAssuredBasedBenefits, benefitId),
    planBasedBenefits: toggleBenefit(enrollmentDetail.planBasedBenefits, benefitId),
    planBasedOptionalBenefits: toggleBenefit(enrollmentDetail.planBasedOptionalBenefits, benefitId),
  };
};
export const updateBenefitsForShowMoreToggle = (enrollmentDetail, benefitId) => {
  return {
    ...enrollmentDetail,
    planBasedConvertLeaveBenefits: toggleShowMore(
      enrollmentDetail.planBasedConvertLeaveBenefits,
      benefitId
    ),
    sumAssuredBasedBenefits: toggleShowMore(enrollmentDetail.sumAssuredBasedBenefits, benefitId),
    planBasedBenefits: toggleShowMore(enrollmentDetail.planBasedBenefits, benefitId),
    planBasedOptionalBenefits: toggleShowMore(
      enrollmentDetail.planBasedOptionalBenefits,
      benefitId
    ),
  };
};

const toggleShowMore = (benefits, benefitId) => {
  return benefits.map(benefit => {
    if (benefit.benefitID == benefitId) {
      return {
        ...benefit,
        isShownMore: !benefit.isShownMore,
      };
    }
    return benefit;
  });
};

const toggleBenefit = (benefits, benefitId) => {
  return benefits.map(benefit => {
    if (benefit.benefitID == benefitId) {
      return {
        ...benefit,
        isExpanded: !benefit.isExpanded,
      };
    }
    return benefit;
  });
};

const applyLinkLogic = (data, newBenefitId, newPlan, newMemberType) => {
  const linkedSumAssured = applyLinkLogicForSumAssured(
    data.sumAssuredBasedBenefits,
    newBenefitId,
    newPlan,
    newMemberType
  );
  const linkedPlanBased = applyLinkLogicForPlanBased(
    data.planBasedBenefits,
    newBenefitId,
    newPlan,
    newMemberType
  );
  const linkedPlanBasedOptional = applyLinkLogicForPlanBased(
    data.planBasedOptionalBenefits,
    newBenefitId,
    newPlan,
    newMemberType
  );

  return {
    ...data,
    sumAssuredBasedBenefits: linkedSumAssured,
    planBasedBenefits: linkedPlanBased,
    planBasedOptionalBenefits: linkedPlanBasedOptional,
  };
};

const applyLinkLogicForSumAssured = (benefits, newBenefitId, newPlan, newMemberType) => {
  if (newBenefitId) {
    const selectedBenefit = benefits.find(x => x.benefitID === newBenefitId);
    if (!selectedBenefit) return benefits;
    if (
      (!selectedBenefit.benefitOptions.linkedParents ||
        selectedBenefit.benefitOptions.linkedParents.length <= 0) &&
      (!selectedBenefit.benefitOptions.rhsBenefits ||
        selectedBenefit.benefitOptions.rhsBenefits.length <= 0)
    )
      return benefits;
  }
  let updatedBenefits = JSON.parse(JSON.stringify(benefits));

  benefits.forEach(benefit => {
    if (benefit.benefitID !== newBenefitId) {
      updatedBenefits = updateBenefitForSumAssuredLinkLogic(
        benefit,
        benefits,
        updatedBenefits,
        newBenefitId,
        newPlan,
        newMemberType
      );
    }
  });

  if (newBenefitId) {
    const latestBenefit = benefits.find(x => x.benefitID === newBenefitId);
    latestBenefit &&
      (updatedBenefits = updateBenefitForSumAssuredLinkLogic(
        latestBenefit,
        benefits,
        updatedBenefits,
        newBenefitId,
        newPlan,
        newMemberType
      ));
  }

  return updatedBenefits;
};

const updateBenefitForSumAssuredLinkLogic = (
  benefit,
  benefits,
  updatedBenefits,
  newBenefitId,
  newPlan,
  newMemberType
) => {
  const parentSelectedPlan = benefit.benefitOptions.options.find(x => x.isSelected);
  if (!parentSelectedPlan) return updatedBenefits;
  const isParentIntervalOptionPlan =
    Array.isArray(parentSelectedPlan.intervalOptions) &&
    parentSelectedPlan.intervalOptions.length > 0;
  const selectedPlanId = getSelectedPlanId(parentSelectedPlan);
  const dependantOptions = isParentIntervalOptionPlan
    ? parentSelectedPlan.dependantOptions.filter(d => d.optionID == selectedPlanId)
    : parentSelectedPlan.dependantOptions;

  Array.isArray(dependantOptions) &&
    dependantOptions.length > 0 &&
    dependantOptions.forEach(dependency => {
      const linkedChildren = getLinkedChildren(benefit, benefits);
      const linkedOptions = dependency.linkedOptions;
      if (linkedChildren.length > 0) {
        updatedBenefits = updatedBenefits.map(x => {
          if (x.benefitID === dependency.benefitID) {
            const linkedBenefit = linkedChildren.find(
              l => l.childBenefitId === x.benefitID && l.benefitID === benefit.benefitID
            );
            if (linkedBenefit) {
              return getSumAssuredBenefitByLinkLogicRule(
                x,
                linkLogicRelationships[linkedBenefit.dependancyType],
                linkedOptions,
                parentSelectedPlan,
                newBenefitId,
                newPlan,
                benefit
              );
            }
          }
          return { ...x };
        });
      }
    });
  return updatedBenefits;
};

const applyLinkLogicForPlanBased = (benefits, newBenefitId, newPlan, newMemberType) => {
  if (newBenefitId) {
    const selectedBenefit = benefits.find(x => x.benefitID === newBenefitId);
    if (!selectedBenefit) return benefits;
    if (
      (!selectedBenefit.benefitOptions.linkedParents ||
        selectedBenefit.benefitOptions.linkedParents.length <= 0) &&
      (!selectedBenefit.benefitOptions.rhsBenefits ||
        selectedBenefit.benefitOptions.rhsBenefits.length <= 0)
    )
      return benefits;
  }

  let updatedBenefits = JSON.parse(JSON.stringify(benefits));
  benefits.forEach(
    benefit =>
      (updatedBenefits = updateBenefitForPlanBasedLinkLogic(
        benefit,
        benefits,
        updatedBenefits,
        newBenefitId,
        newPlan,
        newMemberType
      ))
  );

  //Apply link logic to GrandChild
  benefits.forEach(benefit => {
    if (benefit.benefitOptions.rhsBenefits && benefit.benefitOptions.rhsBenefits.length > 0) {
      benefit.benefitOptions.rhsBenefits.forEach(childBen => {
        if (childBen.children && childBen.children.length > 0) {
          let clonedBenefits = JSON.parse(JSON.stringify(updatedBenefits));

          childBen.children.forEach(grandChildBen => {
            const parentBenefit = clonedBenefits.find(x => x.benefitID === childBen.benefitID);

            const parentSelectedGroup = parentBenefit.benefitOptions.options.find(
              x => x.isSelected
            );
            const parentSelectedPlan =
              parentSelectedGroup && parentSelectedGroup.plans.find(x => x.isSelected);
            const dependantOptions = parentSelectedPlan && parentSelectedPlan.dependantOptions;
            Array.isArray(dependantOptions) &&
              dependantOptions.forEach(dependency => {
                const linkedOptions = dependency.linkedOptions;

                updatedBenefits = updatedBenefits.map(x => {
                  if (x.benefitID === dependency.benefitID) {
                    return getPlanBasedBenefitByLinkLogicRule(
                      x,
                      linkLogicRelationships[grandChildBen.dependancyType],
                      linkedOptions,
                      parentSelectedGroup,
                      parentBenefit.benefitID,
                      parentSelectedPlan,
                      parentSelectedPlan.memberType
                    );
                  }
                  return { ...x };
                });
              });
          });
        }
      });
    }
  });

  return updatedBenefits;
};

const updateBenefitForPlanBasedLinkLogic = (
  benefit,
  benefits,
  updatedBenefits,
  newBenefitId,
  newPlan,
  newMemberType
) => {
  const parentSelectedGroup = benefit.benefitOptions.options.find(x => x.isSelected);
  const parentSelectedPlan =
    parentSelectedGroup && parentSelectedGroup.plans.find(x => x.isSelected);
  const dependantOptions = parentSelectedPlan && parentSelectedPlan.dependantOptions;
  Array.isArray(dependantOptions) &&
    dependantOptions.forEach(dependency => {
      const linkedOptions = dependency.linkedOptions;
      const linkedChildren = getLinkedChildren(benefit, benefits);

      if (linkedChildren.length > 0) {
        updatedBenefits = updatedBenefits.map(x => {
          if (
            x.benefitID === dependency.benefitID &&
            (!newBenefitId ||
              x.benefitID === newBenefitId ||
              x.benefitOptions.linkedParents.find(p => p.benefitID === newBenefitId))
          ) {
            const linkedBenefit = linkedChildren.find(
              l => l.childBenefitId === x.benefitID && l.benefitID === benefit.benefitID
            );
            return (
              linkedBenefit &&
              getPlanBasedBenefitByLinkLogicRule(
                x,
                linkLogicRelationships[linkedBenefit.dependancyType],
                linkedOptions,
                parentSelectedGroup,
                newBenefitId,
                newPlan,
                newMemberType
              )
            );
          }
          return { ...x };
        });
      }
    });

  return updatedBenefits;
};

const getPlanBasedBenefitByLinkLogicRule = (
  benefit,
  rule,
  linkedOptions,
  parentSelectedGroup,
  newBenefitId,
  newPlan,
  newMemberType
) => {
  const parentSelectedPlan =
    parentSelectedGroup &&
    Array.isArray(parentSelectedGroup.plans) &&
    parentSelectedGroup.plans.find(x => x.isSelected);
  switch (rule) {
    case linkLogicRelationships.By1To1MatchPlanID:
      return byOneToOneMatchPlanIdForPlanBased(
        benefit,
        linkedOptions,
        parentSelectedGroup,
        newBenefitId
      );
    case linkLogicRelationships.BySameGroupValueOrNoCoverage:
      return bySameGroupValueOrNoCoverage(
        benefit,
        linkedOptions,
        parentSelectedGroup,
        newBenefitId,
        newPlan
      );
    case linkLogicRelationships.BySameGroupValueOrNoCoverageAndLessEqualPlanValue:
      return bySameGroupValueOrNoCoverageAndLessEqualPlanValue(
        benefit,
        linkedOptions,
        parentSelectedGroup,
        newBenefitId,
        newPlan
      );
    case linkLogicRelationships.ByPlanValue:
      return byPlanValue(
        benefit,
        linkedOptions,
        parentSelectedPlan,
        newBenefitId,
        newPlan,
        newMemberType
      );
    case linkLogicRelationships.ByEqualPlanValue:
      return byEqualPlanValue(benefit, linkedOptions, parentSelectedGroup);
    case linkLogicRelationships.ByPlanAndGroupValue:
      return byPlanAndGroupValue(
        benefit,
        linkedOptions,
        parentSelectedGroup,
        newBenefitId,
        newPlan,
        newMemberType
      );
    case linkLogicRelationships.ByGroupValue:
      return byGroupValue(
        benefit,
        linkedOptions,
        parentSelectedGroup,
        newBenefitId,
        newPlan,
        newMemberType
      );
    case linkLogicRelationships.BySameGroupValueAndLessEqualPlanValue:
      return bySameGroupValueAndLessEqualPlanValue(
        benefit,
        linkedOptions,
        parentSelectedGroup,
        newBenefitId,
        newPlan
      );
    default:
      return benefit;
  }
};

const getSumAssuredBenefitByLinkLogicRule = (
  benefit,
  rule,
  linkedOptions,
  parentSelectedPlan,
  newBenefitId,
  newPlan,
  parentBenefit
) => {
  switch (rule) {
    case linkLogicRelationships.By1To1MatchPlanID:
      return byOneToOneMatchPlanIdForSumAssured(
        benefit,
        linkedOptions,
        parentSelectedPlan,
        newBenefitId,
        newPlan
      );
    case linkLogicRelationships.BySameGroupValueAndLessOrNoCoverage:
      return bySameGroupValueAndLessOrNoCoverageSumAssured(
        benefit,
        linkedOptions,
        parentSelectedPlan,
        newBenefitId,
        newPlan
      );
    case linkLogicRelationships.BySumAssuredAmount:
      return bySumAssuredAmount(
        benefit,
        linkedOptions,
        newBenefitId,
        newPlan,
        parentSelectedPlan,
        parentBenefit
      );
    case linkLogicRelationships.BySumAssuredAmountGreater:
      return bySumAssuredAmountGreater(benefit, linkedOptions);
    default:
      break;
  }
};
const byOneToOneMatchPlanIdForPlanBased = (
  benefit,
  linkedOptions,
  parentSelectedGroup,
  newBenefitId
) => {
  const parentSelectedPlan =
    parentSelectedGroup && parentSelectedGroup.plans.find(x => x.isSelected);
  return {
    ...benefit,
    benefitOptions: {
      ...benefit.benefitOptions,
      options: benefit.benefitOptions.options.map(o => {
        const disabledOptions = o.plans.filter(p => linkedOptions.find(l => l.optionID == p.id));
        const isGroupAvailable =
          (o.groupValue === "0" && disabledOptions.length === 0) ||
          o.groupValue === parentSelectedGroup.groupValue;
        const isGroupSelected =
          !newBenefitId || newBenefitId === benefit.benefitID
            ? o.isSelected
            : o.groupValue === parentSelectedGroup.groupValue;
        return {
          ...o,
          isSelected: isGroupSelected,
          isAvailable: isGroupAvailable,
          plans: o.plans.map(p => {
            if (linkedOptions.some(l => l.optionID == p.id)) {
              return { ...p, isAvailable: false, isSelected: false };
            } else {
              return {
                ...p,
                isAvailable:
                  (o.groupValue === "0" && isGroupSelected) ||
                  p.planValue === parentSelectedPlan.planValue,
                isSelected:
                  (o.groupValue === "0" && isGroupSelected) ||
                  p.planValue === parentSelectedPlan.planValue,
              };
            }
          }),
        };
      }),
    },
  };
};
const byOneToOneMatchPlanIdForSumAssured = (
  benefit,
  linkedOptions,
  parentSelectedPlan,
  newBenefitId,
  newPlan
) => {
  const currentSelectedPlan = benefit.benefitOptions.options.find(x => x.isSelected);

  return {
    ...benefit,
    benefitOptions: {
      ...benefit.benefitOptions,
      options: benefit.benefitOptions.options.map(o => {
        const isAvailable = !linkedOptions.some(l => l.optionID == o.id);
        const isDefault = !newBenefitId && !newPlan;

        const isSelected = isDefault
          ? currentSelectedPlan.id == o.id
          : isAvailable && newBenefitId !== benefit.benefitID && o.id == parentSelectedPlan.id;
        return {
          ...o,
          isAvailable: !linkedOptions.some(l => l.optionID == o.id),
          isSelected: newBenefitId === benefit.benefitID ? o.isSelected : isSelected,
        };
      }),
    },
  };
};

const bySameGroupValueAndLessOrNoCoverageSumAssured = (
  benefit,
  linkedOptions,
  parentSelectedPlan,
  newBenefitId,
  newPlan
) => {
  return {
    ...benefit,
    benefitOptions: {
      ...benefit.benefitOptions,
      options: benefit.benefitOptions.options.map(o => {
        const isAvailable = !linkedOptions.some(l => l.optionID == o.id);
        const isDefault = !newBenefitId && !newPlan;
        const isSelectedForSameBenefit =
          isAvailable && newBenefitId === benefit.benefitID ? o.isSelected : false;
        const isSelected =
          isAvailable && newBenefitId !== benefit.benefitID
            ? parentSelectedPlan.groupValue === o.groupValue
            : isSelectedForSameBenefit;
        return {
          ...o,
          isAvailable: !linkedOptions.some(l => l.optionID == o.id),
          isSelected: isDefault ? o.isSelected : isSelected,
        };
      }),
    },
  };
};

const bySameGroupValueOrNoCoverage = (
  benefit,
  linkedOptions,
  parentSelectedGroup,
  newBenefitId,
  newPlanGroup
) => {
  const currentSelectedGroup = benefit.benefitOptions.options.find(x => x.isSelected);
  const currentSelectedPlan =
    currentSelectedGroup && currentSelectedGroup.plans.find(x => x.isSelected);
  const parentSelectedPlan = parentSelectedGroup.plans.find(x => x.isSelected);

  let selectedMemberGroup;
  if (currentSelectedPlan) {
    selectedMemberGroup = currentSelectedPlan.memberType;
  } else if (parentSelectedPlan) {
    selectedMemberGroup = parentSelectedPlan.memberType;
  } else {
    selectedMemberGroup = currentSelectedGroup.plans[0].memberType;
  }

  return {
    ...benefit,
    benefitOptions: {
      ...benefit.benefitOptions,
      options: benefit.benefitOptions.options.map(o => {
        const disabledOptions = o.plans.filter(p => linkedOptions.find(l => l.optionID == p.id));
        const isDefaultSelection = !newBenefitId && !newPlanGroup;
        const isSameBenefitChanged =
          (newBenefitId && newPlanGroup && newBenefitId === benefit.benefitID) || false;
        let isGroupSelected;

        if (isDefaultSelection) {
          if (currentSelectedGroup && currentSelectedGroup.groupValue === "0") {
            isGroupSelected = o.groupValue === currentSelectedGroup.groupValue;
          } else {
            isGroupSelected = o.isSelected;
          }
        } else if (isSameBenefitChanged) {
          isGroupSelected =
            o.groupValue === newPlanGroup.groupValue && o.groupID === newPlanGroup.groupID;
        } else if (currentSelectedGroup && currentSelectedGroup.groupValue === "0") {
          isGroupSelected = o.groupValue === "0";
        } else {
          isGroupSelected =
            o.groupValue === parentSelectedGroup.groupValue &&
            o.groupID === parentSelectedGroup.groupID;
        }
        if (o.groupValue === "0" && disabledOptions.length === 0) {
          return {
            ...o,
            isAvailable: true,
            isSelected: isGroupSelected,
            plans: o.plans.map(p => {
              return { ...p, isAvailable: true };
            }),
          };
        } else if (disabledOptions.length == 0) {
          return {
            ...o,
            isAvailable: true,
            isSelected: isGroupSelected,
            plans: o.plans.map(p => {
              return { ...p, isAvailable: true, isSelected: p.memberType === selectedMemberGroup };
            }),
          };
        } else if (disabledOptions.length > 0 && o.plans.length == disabledOptions.length) {
          return {
            ...o,
            isAvailable: false,
            isSelected: false,
            plans: o.plans.map(p => {
              return { ...p, isAvailable: false, isSelected: false };
            }),
          };
        } else return { ...o };
      }),
    },
  };
};
const bySameGroupValueOrNoCoverageAndLessEqualPlanValue = (
  benefit,
  linkedOptions,
  parentSelectedGroup,
  newBenefitId,
  newPlanGroup
) => {
  const currentSelectedGroup = benefit.benefitOptions.options.find(x => x.isSelected);
  const currentSelectedPlan = currentSelectedGroup.plans.find(x => x.isSelected);
  const parentSelectedPlan = parentSelectedGroup.plans.find(x => x.isSelected);
  const selectedMemberGroup = currentSelectedPlan
    ? currentSelectedPlan.memberType
    : parentSelectedPlan
    ? parentSelectedPlan.memberType
    : currentSelectedGroup.plans[0].memberType;

  return {
    ...benefit,
    benefitOptions: {
      ...benefit.benefitOptions,
      options: benefit.benefitOptions.options.map(o => {
        const disabledOptions = o.plans.filter(p => linkedOptions.find(l => l.optionID == p.id));
        const isDefaultSelection = !newBenefitId && !newPlanGroup;
        const isSameBenefitChanged =
          (newBenefitId && newPlanGroup && newBenefitId === benefit.benefitID) || false;
        const isAvailable = parentSelectedGroup.groupValue == o.groupValue || o.groupValue === "0";

        let isGroupSelected;

        if (isDefaultSelection) {
          if (currentSelectedGroup.groupValue === "0") {
            isGroupSelected = o.groupValue === currentSelectedGroup.groupValue;
          } else {
            isGroupSelected = o.isSelected;
          }
        } else if (isSameBenefitChanged) {
          isGroupSelected =
            o.groupValue === newPlanGroup.groupValue && o.groupID === newPlanGroup.groupID;
        } else if (currentSelectedGroup.groupValue === "0") {
          isGroupSelected = o.groupValue === "0";
        } else {
          isGroupSelected =
            o.groupValue === parentSelectedGroup.groupValue &&
            o.groupID === parentSelectedGroup.groupID;
        }

        if (disabledOptions.length > 0) {
          return {
            ...o,
            isAvailable: isAvailable,
            isSelected: isGroupSelected,
            plans: o.plans.map(p => {
              if (linkedOptions.some(l => l.optionID == p.id)) {
                return { ...p, isAvailable: false, isSelected: false };
              } else {
                const isSelectedForSelfChanged =
                  newPlanGroup && currentSelectedGroup.groupValue == newPlanGroup.groupValue
                    ? selectedMemberGroup == p.memberType
                    : p.isSelected;
                let isSelectedForParentChanged = false;
                if (currentSelectedPlan) {
                  if (linkedOptions.some(l => l.optionID == currentSelectedPlan.id)) {
                    isSelectedForParentChanged = p.planValue === parentSelectedPlan.planValue;
                  } else {
                    isSelectedForParentChanged = p.planValue === currentSelectedPlan.planValue;
                  }
                }

                return {
                  ...p,
                  isAvailable: true,
                  isSelected:
                    newBenefitId === benefit.benefitID
                      ? isSelectedForSelfChanged
                      : isSelectedForParentChanged,
                };
              }
            }),
          };
        } else if (disabledOptions.length == 0) {
          return {
            ...o,
            isAvailable: isAvailable,
            isSelected: isGroupSelected,
            plans: o.plans.map(p => {
              let isPlanSelected;
              if (newBenefitId == benefit.benefitID && selectedMemberGroup == p.memberType) {
                isPlanSelected = true;
              } else if (newBenefitId != benefit.benefitID) {
                isPlanSelected =
                  currentSelectedPlan && currentSelectedPlan.memberType == p.memberType;
              } else {
                isPlanSelected = p.isSelected;
              }
              return {
                ...p,
                isAvailable: true,
                isSelected: isPlanSelected,
              };
            }),
          };
        }
        return { ...o };
      }),
    },
  };
};
const byEqualPlanValue = (benefit, linkedOptions, parentSelectedGroup) => {
  const parentSelectedPlan =
    parentSelectedGroup &&
    Array.isArray(parentSelectedGroup.plans) &&
    parentSelectedGroup.plans.find(x => x.isSelected);

  const newBenefit = {
    ...benefit,
    benefitOptions: {
      ...benefit.benefitOptions,
      options: benefit.benefitOptions.options.map(o => {
        const disabledOptions = o.plans.filter(p => linkedOptions.find(l => l.optionID == p.id));

        if (disabledOptions.length > 0 && o.plans.length == disabledOptions.length) {
          return {
            ...o,
            isAvailable: false,
            isSelected: false,
            plans: o.plans.map(p => {
              return { ...p, isAvailable: false, isSelected: false };
            }),
            eligibleMembers: {
              ...o.eligibleMembers,
              members: o.eligibleMembers.members.map(x => {
                if (x.memberType !== "Employee") {
                  return { ...x, selectable: false, isSelected: false };
                } else {
                  return { ...x };
                }
              }),
            },
          };
        } else if (disabledOptions.length > 0 && o.plans.length > disabledOptions.length) {
          return {
            ...o,
            isAvailable: true,
            plans: o.plans.map(p => {
              if (linkedOptions.some(l => l.optionID == p.id)) {
                return { ...p, isAvailable: false, isSelected: false };
              } else {
                return {
                  ...p,
                  isAvailable: p.memberType === parentSelectedPlan.memberType,
                  isSelected:
                    p.planValue === parentSelectedPlan.planValue &&
                    p.memberType === parentSelectedPlan.memberType,
                };
              }
            }),
            eligibleMembers: {
              ...o.eligibleMembers,
              members: o.eligibleMembers.members.map(x => {
                if (x.memberType !== "Employee") {
                  return {
                    ...x,
                    selectable: false,
                    isSelected: parentSelectedGroup.eligibleMembers.members.some(
                      m => m.key === x.key && m.type === x.type && m.isSelected
                    ),
                  };
                } else {
                  return { ...x };
                }
              }),
            },
          };
        } else if (disabledOptions.length == 0 && o.groupID === "0" && o.plans.length === 1) {
          return {
            ...o,
            isAvailable: true,
            isSelected: true,
          };
        }
        return { ...o };
      }),
    },
  };
  const newSelectedSelectedGroup = newBenefit.benefitOptions.options.find(x => x.isSelected);
  if (!newSelectedSelectedGroup) {
    let availableGroups = newBenefit.benefitOptions.options.filter(x => x.groupID !== "0");
    availableGroups && availableGroups.length > 0 && (availableGroups[0].isSelected = true);
  }

  return newBenefit;
};

const byPlanValue = (
  benefit,
  linkedOptions,
  parentSelectedPlan,
  newBenefitId,
  newPlanGroup,
  selectedMember
) => {
  const currentSelectedGroup = benefit.benefitOptions.options.find(x => x.isSelected);
  const currentMemberGroup =
    currentSelectedGroup && currentSelectedGroup.plans.find(x => x.isSelected);
  const isDefaultSelection = !newBenefitId && !newPlanGroup;

  return {
    ...benefit,
    benefitOptions: {
      ...benefit.benefitOptions,
      options: benefit.benefitOptions.options.map(o => {
        const disabledOptions = o.plans.filter(p => linkedOptions.find(l => l.optionID == p.id));

        if (disabledOptions.length > 0 && o.plans.length == disabledOptions.length) {
          return {
            ...o,
            isAvailable: false,
            isSelected: false,
            plans: o.plans.map(p => {
              return { ...p, isAvailable: false, isSelected: false };
            }),
          };
        } else if (disabledOptions.length > 0 && o.plans.length > disabledOptions.length) {
          return {
            ...o,
            isAvailable: true,
            plans: o.plans.map(p => {
              if (linkedOptions.some(l => l.optionID == p.id)) {
                return { ...p, isAvailable: false, isSelected: false };
              } else {
                let isPlanSelected;
                if (isDefaultSelection) {
                  isPlanSelected = p.isSelected;
                } else if (newBenefitId != benefit.benefitID) {
                  isPlanSelected = linkedOptions.some(l => l.optionID == currentMemberGroup.id)
                    ? p.memberType === parentSelectedPlan.memberType
                    : p.memberType === currentMemberGroup.memberType;
                } else if (
                  newPlanGroup &&
                  currentSelectedGroup.groupValue === newPlanGroup.groupValue
                ) {
                  isPlanSelected = p.isSelected;
                } else {
                  isPlanSelected = selectedMember === p.memberType;
                }
                return {
                  ...p,
                  isAvailable: true,
                  isSelected: isPlanSelected,
                };
              }
            }),
          };
        } else if (disabledOptions.length == 0) {
          return {
            ...o,
            isAvailable: true,
            plans: o.plans.map(p => {
              let isPlanSelected;
              if (
                isDefaultSelection ||
                (currentMemberGroup.memberType === "00" && p.memberType == "00")
              ) {
                isPlanSelected = p.isSelected;
              } else if (newBenefitId != benefit.benefitID) {
                isPlanSelected = linkedOptions.some(l => l.optionID == currentMemberGroup.id)
                  ? p.memberType === parentSelectedPlan.memberType
                  : p.memberType === currentMemberGroup.memberType;
              } else if (
                newPlanGroup &&
                currentSelectedGroup.groupValue === newPlanGroup.groupValue
              ) {
                isPlanSelected = p.isSelected;
              } else {
                isPlanSelected = selectedMember === p.memberType;
              }
              return {
                ...p,
                isAvailable: true,
                isSelected: isPlanSelected,
              };
            }),
          };
        }
        return { ...o };
      }),
    },
  };
};

const byPlanAndGroupValue = (
  benefit,
  linkedOptions,
  parentSelectedGroup,
  newBenefitId,
  newPlanGroup,
  selectedMember
) => {
  const currentSelectedGroup = benefit.benefitOptions.options.find(x => x.isSelected);
  const currentMemberGroup = currentSelectedGroup.plans.find(x => x.isSelected);
  const parentSelectedPlan =
    parentSelectedGroup &&
    Array.isArray(parentSelectedGroup.plans) &&
    parentSelectedGroup.plans.find(x => x.isSelected);

  return {
    ...benefit,
    benefitOptions: {
      ...benefit.benefitOptions,
      options: benefit.benefitOptions.options.map(o => {
        const disabledOptions = o.plans.filter(p => linkedOptions.find(l => l.optionID == p.id));
        let isGroupSelected;

        if (newBenefitId !== benefit.benefitID) {
          isGroupSelected =
            parentSelectedGroup.groupValue === o.groupValue &&
            parentSelectedGroup.groupID === o.groupID;
        } else if (newBenefitId === benefit.benefitID) {
          isGroupSelected =
            newPlanGroup.groupValue === o.groupValue && newPlanGroup.groupID === o.groupID;
        } else {
          isGroupSelected = o.isSelected;
        }

        if (disabledOptions.length > 0 && o.plans.length == disabledOptions.length) {
          return {
            ...o,
            isAvailable: false,
            isSelected: false,
            plans: o.plans.map(p => {
              return { ...p, isAvailable: false, isSelected: false };
            }),
          };
        } else if (disabledOptions.length > 0 && o.plans.length > disabledOptions.length) {
          return {
            ...o,
            isAvailable: true,
            isSelected: isGroupSelected,
            plans: o.plans.map(p => {
              if (linkedOptions.some(l => l.optionID == p.id)) {
                return { ...p, isAvailable: false, isSelected: false };
              } else {
                let isPlanSelected;

                if (newBenefitId != benefit.benefitID) {
                  isPlanSelected = linkedOptions.some(l => l.optionID == currentMemberGroup.id)
                    ? p.planValue === parentSelectedPlan.planValue
                    : p.planValue === currentMemberGroup.planValue;
                } else if (
                  newPlanGroup &&
                  currentSelectedGroup.groupValue === newPlanGroup.groupValue
                ) {
                  isPlanSelected = p.isSelected;
                } else {
                  isPlanSelected = selectedMember === p.memberType;
                }
                return {
                  ...p,
                  isAvailable: true,
                  isSelected: isPlanSelected,
                };
              }
            }),
          };
        } else if (disabledOptions.length == 0) {
          return {
            ...o,
            isAvailable: true,
            isSelected: isGroupSelected,
            plans: o.plans.map(p => {
              let isPlanSelected;

              if (newBenefitId !== benefit.benefitID) {
                isPlanSelected =
                  currentMemberGroup && currentMemberGroup.memberType === p.memberType
                    ? true
                    : p.isSelected;
              } else if (newBenefitId === benefit.benefitID && selectedMember === p.memberType) {
                isPlanSelected = true;
              } else {
                isPlanSelected =
                  currentMemberGroup && currentMemberGroup.memberType === p.memberType;
              }
              return {
                ...p,
                isAvailable: true,
                isSelected: isPlanSelected,
              };
            }),
          };
        }
        return { ...o };
      }),
    },
  };
};

const byGroupValue = (
  benefit,
  linkedOptions,
  parentSelectedGroup,
  newBenefitId,
  newPlanGroup,
  selectedMember
) => {
  const currentSelectedGroup = benefit.benefitOptions.options.find(x => x.isSelected);
  const currentMemberGroup =
    currentSelectedGroup && currentSelectedGroup.plans.find(x => x.isSelected);

  return {
    ...benefit,
    benefitOptions: {
      ...benefit.benefitOptions,
      options: benefit.benefitOptions.options.map(o => {
        const disabledOptions = o.plans.filter(p => linkedOptions.find(l => l.optionID == p.id));

        if (disabledOptions.length > 0 && o.plans.length == disabledOptions.length) {
          return {
            ...o,
            isAvailable: false,
            isSelected: false,
            plans: o.plans.map(p => {
              return { ...p, isAvailable: false, isSelected: false };
            }),
          };
        } else if (o.plans.length > disabledOptions.length) {
          const isDefaultSelection = !newBenefitId && !newPlanGroup;
          let isGroupSelected;

          if (newBenefitId !== benefit.benefitID) {
            isGroupSelected =
              parentSelectedGroup.groupValue === o.groupValue &&
              parentSelectedGroup.groupID === o.groupID;
          } else if (newBenefitId === benefit.benefitID) {
            isGroupSelected =
              newPlanGroup.groupValue === o.groupValue && newPlanGroup.groupID === o.groupID;
          } else {
            isGroupSelected = o.isSelected;
          }
          return {
            ...o,
            isAvailable: true,
            isSelected: isGroupSelected,
            plans: o.plans.map(p => {
              if (linkedOptions.some(l => l.optionID == p.id)) {
                return { ...p, isAvailable: false, isSelected: false };
              } else {
                return {
                  ...p,
                  isAvailable: true,
                  isSelected:
                    newBenefitId != benefit.benefitID && !isDefaultSelection
                      ? currentMemberGroup && currentMemberGroup.memberType === p.memberType
                      : p.isSelected,
                };
              }
            }),
          };
        }
        return { ...o };
      }),
    },
  };
};

const bySumAssuredAmount = (
  benefit,
  linkedOptions,
  newBenefitId,
  newPlan,
  parentSelectedPlan,
  parentBenefit
) => {
  const currentSelectedPlan = benefit.benefitOptions.options.find(x => x.isSelected);
  const isCurrentIntervalPlan =
    currentSelectedPlan &&
    Array.isArray(currentSelectedPlan.intervalOptions) &&
    currentSelectedPlan.intervalOptions.length > 0;
  const currentSelectedPlanId = isCurrentIntervalPlan && getSelectedPlanId(currentSelectedPlan);

  const currentSelectedIndex =
    currentSelectedPlanId && currentSelectedPlanId.replace(currentSelectedPlan.id, "");
  const isParentPerMemberSelection =
    parentSelectedPlan.eligibleMembers.selectionMode === "Individual";
  const isCurrentPerMemberSelection =
    currentSelectedPlan &&
    currentSelectedPlan.eligibleMembers &&
    currentSelectedPlan.eligibleMembers.selectionMode === "Individual";
  let isCurrentSelectionValid = true;
  let updatedBenefit = {
    ...benefit,
    benefitOptions: {
      ...benefit.benefitOptions,
      options: benefit.benefitOptions.options.map(o => {
        const isIntervalOptionPlan =
          Array.isArray(o.intervalOptions) && o.intervalOptions.length > 0;
        const availableIntervalOptions = isIntervalOptionPlan
          ? o.originalIntervalOptions.filter(
              x => !linkedOptions.some(l => l.optionID == o.id + x.interval)
            )
          : null;
        const intervalOptionsObj = {};
        if (
          isIntervalOptionPlan &&
          Array.isArray(availableIntervalOptions) &&
          availableIntervalOptions.length > 0
        ) {
          if (
            currentSelectedIndex &&
            availableIntervalOptions.find(x => x.interval == currentSelectedIndex)
          ) {
            intervalOptionsObj.intervalOptions = availableIntervalOptions.map(x =>
              x.interval == currentSelectedIndex ? { ...x, isSelected: true } : { ...x }
            );
          } else {
            intervalOptionsObj.intervalOptions = availableIntervalOptions.map(x =>
              x.interval == 0 ? { ...x, isSelected: true } : { ...x }
            );
            if (o.isSelected) {
              isCurrentSelectionValid = false;
            }
          }
        } else {
          intervalOptionsObj.intervalOptions = o.originalIntervalOptions;
        }

        const isAvailable = isIntervalOptionPlan
          ? Array.isArray(availableIntervalOptions) && availableIntervalOptions.length > 0
          : !linkedOptions.some(l => l.optionID == o.id);
        const isSelectedOnSameBenefit =
          newBenefitId == benefit.benefitID && newPlan && newPlan.id == o.id ? true : o.isSelected;
        const isSelected =
          !isAvailable || !isCurrentSelectionValid ? false : isSelectedOnSameBenefit;
        const eligibleMembers = isParentPerMemberSelection
          ? {
              ...o.eligibleMembers,
              members: o.eligibleMembers.members.map(x => {
                if (x.memberType !== "Employee") {
                  const isSelectable = parentSelectedPlan.eligibleMembers.members.some(
                    m => m.key === x.key && m.type === x.type && m.isSelected
                  );
                  return {
                    ...x,
                    selectable: isSelectable,
                    isSelected: !isSelectable ? false : x.isSelected,
                  };
                } else {
                  return { ...x };
                }
              }),
            }
          : { ...o.eligibleMembers };
        const selectedMembers = eligibleMembers.members.filter(m => m.isSelected);
        let selectedIntervalOption =
          isCurrentIntervalPlan &&
          Array.isArray(intervalOptionsObj.intervalOptions) &&
          intervalOptionsObj.intervalOptions.find(x => x.isSelected);
        if (isIntervalOptionPlan && !selectedIntervalOption) {
          selectedIntervalOption = intervalOptionsObj.intervalOptions[0];
        }
        const accumulatedPrice =
          selectedIntervalOption &&
          selectedIntervalOption.memberPrices.reduce(
            (accumulator, memberPrice) => {
              if (selectedMembers.find(x => x.key === memberPrice.memberKey)) {
                return {
                  numericValue:
                    Number(accumulator.numericValue || 0) + Number(memberPrice.price.numericValue),
                  value: Number(accumulator.value || 0) + Number(memberPrice.price.value),
                };
              }
              return accumulator;
            },
            { numericValue: 0, value: 0 }
          );
        const allInOnePrice = selectedIntervalOption
          ? { ...selectedIntervalOption.price }
          : { ...o.pricing.price };
        const pricing = {
          ...o.pricing,
          price: isCurrentPerMemberSelection ? { ...accumulatedPrice } : allInOnePrice,
        };
        return {
          ...o,
          isAvailable: isAvailable,
          isSelected: isSelected,
          eligibleMembers,
          pricing,
          planValue: selectedIntervalOption
            ? { ...selectedIntervalOption.planValue }
            : { ...o.planValue },
          requiresUW: selectedIntervalOption ? selectedIntervalOption.requiresUW : o.requiresUW,
          ...intervalOptionsObj,
        };
      }),
    },
  };
  if (
    !isCurrentSelectionValid ||
    !updatedBenefit.benefitOptions.options.find(x => x.isSelected) ||
    updatedBenefit.benefitOptions.options.filter(x => x.isSelected).length > 1
  ) {
    if (newBenefitId !== benefit.benefitID) {
      alert(
        `${benefit.benefitName} option selected is invalid due to the change in selection for ${parentBenefit.benefitName} and it will be reset to the default coverage. Please select a valid option for ${benefit.benefitName}.`
      );
    } else if (updatedBenefit.benefitOptions.options.filter(x => x.isSelected).length <= 1) {
      alert(
        `You are trying to select an invalid option for ${benefit.benefitName}, which is restricted due to your selection for ${parentBenefit.benefitName}, and it will be reset to the Default coverage.`
      );
    }
    updatedBenefit = resetSumAssuredBenefit(updatedBenefit);
  }
  return updatedBenefit;
};

const resetSumAssuredBenefit = benefit => {
  return {
    ...benefit,
    benefitOptions: {
      ...benefit.benefitOptions,
      options: benefit.benefitOptions.options.map(o => {
        let defaultPlanId = benefit.benefitOptions.defaultOption.id;
        let defaultPlan = benefit.benefitOptions.options.find(x => x.id === defaultPlanId);
        if (
          !defaultPlan &&
          defaultPlanId.length > 2 &&
          defaultPlanId.charAt(defaultPlanId.length - 2) === "i"
        ) {
          defaultPlanId = defaultPlanId.substring(0, defaultPlanId.length - 1);
          defaultPlan = benefit.benefitOptions.options.find(x => x.id === defaultPlanId);
        }
        return {
          ...o,
          isSelected: o.isAvailable && o.id === defaultPlan.id,
        };
      }),
    },
  };
};
const bySumAssuredAmountGreater = (benefit, linkedOptions) => {
  return {
    ...benefit,
    benefitOptions: {
      ...benefit.benefitOptions,
      options: benefit.benefitOptions.options.map(o => {
        const isAvailable = !linkedOptions.some(l => l.optionID == o.id);
        const isSelected = !isAvailable ? false : o.isSelected;
        return {
          ...o,
          isAvailable: isAvailable,
          isSelected: isSelected,
        };
      }),
    },
  };
};

const bySameGroupValueAndLessEqualPlanValue = (
  benefit,
  linkedOptions,
  parentSelectedGroup,
  newBenefitId,
  newPlanGroup
) => {
  const currentSelectedGroup = benefit.benefitOptions.options.find(x => x.isSelected);
  const currentSelectedPlan = currentSelectedGroup.plans.find(x => x.isSelected);
  const parentSelectedPlan = parentSelectedGroup.plans.find(x => x.isSelected);
  const selectedMemberGroup = currentSelectedPlan
    ? currentSelectedPlan.memberType
    : parentSelectedPlan
    ? parentSelectedPlan.memberType
    : currentSelectedGroup.plans[0].memberType;
  const hasDuplicateGroupValues = hasDuplicateItems(benefit.benefitOptions.options, "groupValue");
  return {
    ...benefit,
    benefitOptions: {
      ...benefit.benefitOptions,
      options: benefit.benefitOptions.options.map(o => {
        const disabledOptions = o.plans.filter(p => linkedOptions.find(l => l.optionID == p.id));
        const isDefaultSelection = !newBenefitId && !newPlanGroup;
        const isSameBenefitChanged =
          (newBenefitId && newPlanGroup && newBenefitId === benefit.benefitID) || false;
        const isAvailable = parentSelectedGroup.groupValue == o.groupValue;

        let isGroupSelected;

        if (isDefaultSelection) {
          isGroupSelected = o.isSelected;
        } else if (isSameBenefitChanged) {
          isGroupSelected = hasDuplicateGroupValues
            ? o.groupValue === newPlanGroup.groupValue && o.groupID === newPlanGroup.groupID
            : o.groupValue === newPlanGroup.groupValue;
        } else {
          isGroupSelected = hasDuplicateGroupValues
            ? o.groupValue === parentSelectedGroup.groupValue &&
              o.groupID === parentSelectedGroup.groupID
            : o.groupValue === parentSelectedGroup.groupValue;
        }

        if (disabledOptions.length > 0) {
          return {
            ...o,
            isAvailable: isAvailable,
            isSelected: isGroupSelected,
            plans: o.plans.map(p => {
              if (linkedOptions.some(l => l.optionID == p.id)) {
                return { ...p, isAvailable: false, isSelected: false };
              } else {
                const isSelectedForParentChanged = linkedOptions.some(
                  l => l.optionID == currentSelectedPlan.id
                )
                  ? p.planValue === parentSelectedPlan.planValue
                  : p.planValue === currentSelectedPlan.planValue;
                const isSelectedForSelfChanged =
                  newPlanGroup && currentSelectedGroup.groupValue == newPlanGroup.groupValue
                    ? selectedMemberGroup == p.memberType
                    : p.isSelected;
                return {
                  ...p,
                  isAvailable: true,
                  isSelected:
                    newBenefitId != benefit.benefitID
                      ? isSelectedForParentChanged
                      : isSelectedForSelfChanged,
                };
              }
            }),
          };
        } else if (disabledOptions.length == 0) {
          return {
            ...o,
            isAvailable: isAvailable,
            isSelected: isGroupSelected,
            plans: o.plans.map(p => {
              return {
                ...p,
                isAvailable: true,
                isSelected:
                  newBenefitId == benefit.benefitID && selectedMemberGroup == p.memberType
                    ? true
                    : newBenefitId != benefit.benefitID
                    ? currentSelectedPlan && currentSelectedPlan.memberType == p.memberType
                    : p.isSelected,
              };
            }),
          };
        }
        return { ...o };
      }),
    },
  };
};

// Generate child benefits IDs
// option - Contain the plan options of the current selected Benefit.
const getChildIdList = options => {
  const benefitIds = [];
  options.forEach(option => {
    if (option.dependantOptions) {
      option.dependantOptions.forEach(o => {
        if (!benefitIds.includes(o.benefitID) && o.isLinkedBenefitParentBenefit == "false") {
          benefitIds.push(o.benefitID);
        }
      });
    }
  });
  return benefitIds;
};

const getLinkedChildren = (benefit, benefits) => {
  const childIds = benefit.benefitOptions.rhsBenefitID;
  const dependencies = [];
  // If there are no child benefit ids return by the API, do a lookup at dependency options
  // and compose the child ids if have.
  const childIdList = childIds
    ? childIds.split(",")
    : getChildIdList(benefit.benefitOptions.options);
  for (const childId of childIdList) {
    const childBenefit = benefits.find(benefit => benefit.benefitID === childId);
    if (childBenefit) {
      const linkedParents = childBenefit.benefitOptions.linkedParents || [];
      linkedParents.forEach(x => {
        if (benefit.benefitID === x.benefitID) {
          dependencies.push({ ...x, childBenefitId: childId });
        }
      });
    }
  }
  return dependencies;
};

export const isPlanRequireUW = plan => {
  if (!plan) return false;
  if (plan.requiresUW == "true") return true;
  if (isGroupPlanPerMembered(plan)) {
    const selectedPlan = plan.plans.find(x => x.isSelected);
    return selectedPlan && selectedPlan.requiresUW == "true";
  }
  if (isPlanMemberGrouped(plan)) {
    const selectedPlan = plan.plans.find(x => x.isSelected);
    return (
      (selectedPlan && selectedPlan.requiresUW == "true") || plan.plans[0].requiresUW == "true"
    );
  }

  if (isGroupPlan(plan)) {
    return plan.plans[0].requiresUW == "true";
  }

  const isIntervalOptions = Array.isArray(plan.intervalOptions) && plan.intervalOptions.length > 0;
  const selectedInterval =
    isIntervalOptions &&
    (plan.intervalOptions.find(option => option.isSelected) || plan.intervalOptions[0]);
  if (selectedInterval) {
    return selectedInterval.requiresUW == "true";
  }
  return false;
};

export const getAllEligibleMembers = plan => {
  let members = [];
  if (
    plan.eligibleMembers &&
    plan.eligibleMembers.members &&
    Array.isArray(plan.eligibleMembers.members)
  ) {
    members = [...plan.eligibleMembers.members];
  }

  if (Array.isArray(plan.plans)) {
    plan.plans.map(p => {
      if (Array.isArray(p.eligibleMembers.members)) {
        p.eligibleMembers.members.map(m => {
          if (!members.find(x => x.key === m.key && x.type === m.type)) {
            members = members.concat(m);
          }
        });
      }
    });
  }
  return members;
};

function hasDuplicateItems(arr, key) {
  let idSet = new Set();
  for (let obj of arr) {
    if (idSet.has(obj[key])) {
      return true; // Found a duplicate id
    } else {
      idSet.add(obj[key]);
    }
  }
  return false;
}



// WEBPACK FOOTER //
// ./components/employee/shop/EnrollmentDataUtil.js